import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { FvButton, FvLinkButton } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { HasQuote } from '@/features/auth/HasPermission'
import { HideForVendor, ShipperOnly } from '@/features/auth/HasRole'
import { WorkflowEnabled } from '@/features/auth/HasWorkflow'
import { useHasPermissionFn, useRole } from '@/features/auth/hooks'
import { useHasAskAboutShipment } from '@/features/chat/api'
import {
  buildShipmentContextMessage,
  PjPromptPopup,
} from '@/features/chat/PjPromptPopup'
import { useShipmentLabels } from '@/features/labels/hooks'
import { useHasUnreadMessagesOnLoad } from '@/features/messages/queries'
import {
  useShowRouteGuide,
  useUnifiedShipmentFlow,
} from '@/features/route-guides/hooks'
import { useArchiveLoads } from '@/hooks/shipments'
import { useBolLinkState } from '@/hooks/shipments/useBolLinkState'
import { composeEditShipmentUrl } from '@/hooks/shipments/useEditBookingPage'
import { routes } from '@/routes'
import { canConfirmShipment } from '@/utils/shipmentFuncs'

import UploadDocumentField from '../inputs/UploadDocumentField'
import { List, ListItem } from '../List'
import type { PanelName } from './LoadDetailPanel'

type Props = {
  setActivePanel: Dispatch<SetStateAction<PanelName>>
  shipment: FullShipment
  showRatesPanel: () => void
}

const ShipmentActions = ({
  setActivePanel,
  shipment,
  showRatesPanel,
}: Props) => {
  const [showUploadBOL, setShowUploadBOL] = useState(false)
  const hasPerm = useHasPermissionFn()
  const canBook = hasPerm('load-booker')
  const canManageFiles = hasPerm('load-file-manager')
  const { status } = shipment ?? {}
  const { archive, canArchive, isArchiving } = useArchiveLoads(status)
  const closeUploadField = useCallback(() => setShowUploadBOL(false), [])
  const labels = useShipmentLabels(shipment)
  const role = useRole()
  const unifiedFlow = useUnifiedShipmentFlow().isEnabled(shipment.workflow)
  const hasUnreadMessages = useHasUnreadMessagesOnLoad({
    loadId: shipment.loadId,
  })
  const hasAsk = useHasAskAboutShipment()
  const showRouteGuide = useShowRouteGuide(shipment)

  const {
    carrierBolUrl,
    fvBolUrl,
    hideFvBolFromSpotQuote,
    canFinishBol,
    isProcessing: isBolProcessing,
  } = useBolLinkState(shipment)

  useEffect(() => {
    setShowUploadBOL(false)
  }, [shipment?.loadId])

  if (!shipment) return null

  const { isArchived, loadId, pickup, isFreightCollect } = shipment
  const isProcessingPickup = pickup?.status === 'requesting'
  const isAwarded = status === 'awarded'
  const isCanceled = status === 'canceled'
  const isConfirmed = status === 'confirmed'
  const isShipper = role === 'shipper'
  const isBooked = isAwarded || isConfirmed
  const canRetender = isBooked && pickup?.method !== 'api'

  const finishBolLink = composeEditShipmentUrl({
    shipment,
    referrer: 'listing',
    mode: 'finishBOL',
    unifiedFlow,
  })
  const canSelectCarrier =
    canRetender && canBook && isShipper && !!showRatesPanel && !isFreightCollect
  const canUploadBol = !isCanceled && canBook && canManageFiles
  const canUpdateTracking =
    canBook && (isConfirmed || status === 'picked-up' || status === 'delivered')

  return (
    <List>
      {canConfirmShipment(shipment) && canBook && (
        <ListItem>
          <FvButton
            onClick={() => {
              !isProcessingPickup && setActivePanel('confirm')
            }}
            icon={isProcessingPickup ? 'spinner' : 'user'}
          >
            {isProcessingPickup ? 'Processing pickup' : 'Confirm this myself'}
          </FvButton>
        </ListItem>
      )}

      {isBolProcessing && (
        <ListItem>
          <FvButton icon="sync">Processing BOL</FvButton>
        </ListItem>
      )}

      {fvBolUrl && !carrierBolUrl && !hideFvBolFromSpotQuote && (
        <ListItem>
          <FvLinkButton
            href={fvBolUrl}
            icon="file-contract"
            rel="noreferrer"
            target="_blank"
          >
            View Freightview BOL
          </FvLinkButton>
        </ListItem>
      )}

      {carrierBolUrl && (
        <ListItem>
          <FvLinkButton
            href={carrierBolUrl}
            icon="file-contract"
            rel="noreferrer"
            target="_blank"
          >
            View Carrier BOL
          </FvLinkButton>
        </ListItem>
      )}

      {canUpdateTracking && (
        <ListItem>
          <FvButton
            onClick={() => {
              setActivePanel('track')
            }}
            icon="thumbtack"
            transform="down-1"
          >
            Update tracking status
          </FvButton>
        </ListItem>
      )}

      {canFinishBol && (
        <ListItem>
          <FvLinkButton icon="file-minus" to={finishBolLink}>
            Finish Freightview BOL
          </FvLinkButton>
        </ListItem>
      )}

      {canUploadBol && (
        <ListItem className="flex-wrap">
          <FvButton onClick={() => setShowUploadBOL(true)} icon="cloud-upload">
            Upload your own BOL
          </FvButton>

          {showUploadBOL && (
            <div className="basis-full mt-4 -mb-2">
              <UploadDocumentField
                defaultDocType="bol"
                handleClose={closeUploadField}
                loadId={loadId}
                onSuccess={closeUploadField}
              />
            </div>
          )}
        </ListItem>
      )}

      {labels.hasLabels && (
        <ListItem>
          <FvLinkButton
            href={labels.href}
            icon={labels.isLoading ? 'sync' : 'tags'}
            onClick={() => {
              if (labels.href || labels.isLoading) return
              setActivePanel('label')
            }}
            rel="noreferrer"
            target="_blank"
          >
            Print labels
          </FvLinkButton>
        </ListItem>
      )}

      {canSelectCarrier && (
        <WorkflowEnabled workflow={shipment.workflow}>
          <ListItem>
            <FvButton onClick={showRatesPanel} icon="truck-moving">
              <span>Select new carrier / Edit charges</span>
            </FvButton>
          </ListItem>
        </WorkflowEnabled>
      )}
      <HideForVendor>
        <HasQuote>
          <ListItem>
            <FvButton
              icon="comment-alt-lines"
              onClick={() => setActivePanel('message')}
              className="relative"
            >
              {hasUnreadMessages && <MessageDot />}
              Messages / Notes / Tags
            </FvButton>
          </ListItem>
        </HasQuote>
        {showRouteGuide && (
          <ListItem>
            <FvButton icon="route" onClick={() => setActivePanel('routeGuide')}>
              Route guide
            </FvButton>
          </ListItem>
        )}
      </HideForVendor>

      <ListItem>
        <FvLinkButton icon="arrow-to-right" to={routes.details(loadId)}>
          More detail / options
        </FvLinkButton>
      </ListItem>
      {hasAsk && (
        <ListItem>
          <PjPromptPopup
            side="left"
            contextMessage={buildShipmentContextMessage(loadId)}
            trigger={<FvLinkButton icon="pallet-jack">Ask PJ</FvLinkButton>}
          />
        </ListItem>
      )}

      {canArchive && (
        <ShipperOnly permissions={['load-booker']}>
          <ListItem>
            <FvButton
              icon={isArchiving ? 'spinner' : 'archive'}
              onClick={() => {
                archive({
                  loadIds: [loadId],
                  shouldArchive: !isArchived,
                })
              }}
            >
              {isArchived ? 'Unarchive' : 'Archive'}
            </FvButton>
          </ListItem>
        </ShipperOnly>
      )}
    </List>
  )
}

export default ShipmentActions

const MessageDot = () => (
  <div className="bg-fv-orange-bright w-1 h-1 absolute -left-3 top-[.35rem] animate-pulse" />
)
